/**
 * Must-Spend vs. Guaranteed Income (spec §13). The gap between the two lines
 * is what the portfolio must cover no matter what — a more useful frame than
 * a single success rate.
 */
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { YearSnapshot } from '../engine/snapshot';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  snapshots: YearSnapshot[];
  todaysDollars: boolean;
}

export function GuaranteedChart({ snapshots, todaysDollars }: Props) {
  const data = snapshots.map((s) => {
    const f = todaysDollars ? s.cumulativeInflationFactor : 1;
    return {
      year: s.year,
      'Guaranteed income': Math.round(s.guaranteedIncome / f),
      // Must-spend includes healthcare and LTC — they are not optional.
      'Must-spend': Math.round(
        (s.essentialSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost) / f,
      ),
    };
  });

  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid stroke="var(--grid)" strokeWidth={1} vertical={false} />
          <XAxis
            dataKey="year"
            stroke="var(--axis)"
            tickLine={false}
            tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
            tickMargin={8}
            minTickGap={28}
          />
          <YAxis
            stroke="var(--axis)"
            tickLine={false}
            axisLine={false}
            tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
            tickFormatter={(v: number) => fmtCompactUsd(v)}
            width={64}
          />
          <Tooltip
            cursor={{ stroke: 'var(--axis)', strokeWidth: 1 }}
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--ink)',
            }}
            formatter={(value) => fmtUsd(Number(value))}
            labelStyle={{ color: 'var(--ink-secondary)', fontWeight: 600 }}
          />
          <Legend wrapperStyle={{ fontSize: 13, color: 'var(--ink-secondary)' }} iconType="plainline" />
          <Line type="monotone" dataKey="Guaranteed income"
            stroke="var(--series-1)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Must-spend"
            stroke="var(--series-2)" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
